<template>
<div class="promotion-container quintpack-promotion">
    <div class="promotion">
        <div class="wordmark" :class="{ 'wordmark-compact': !quintPackActivated }">
            <NuxtImg :src="activeQuintPackCopy?.logoSrc" />
        </div>
        <div class="description">
            <div v-if="!quintPackActivated" class="header">{{ $t('SHOPPING_CART.PROMOTIONS.QUINTPACK.HEADER_INACTIVE') }}</div>
            <div v-else class="header">{{ $t('SHOPPING_CART.PROMOTIONS.QUINTPACK.HEADER_ACTIVE') }}</div>
            <div v-if="!quintPackActivated" class="promotion-inactive">
                <div>{{ quintPackRangeDescription }}</div>
            </div>
            <div v-else class="promotion-active">
                <div>{{ $t('SHOPPING_CART.PROMOTIONS.QUINTPACK.INCLUDES_DESCRIPTION_LINE_1') }}</div>
                <div>{{ quintPackIncludesDescriptionLine2 }}</div>
            </div>
        </div>
        <div class="toggle">
            <label class="switch">
                <input
                    id="quintpack-promotion-active" type="checkbox"
                    :disabled="toggleDisabled"
                    :checked="quintPackActivated"
                    v-on:change="$emit('toggle')"
                >
                <span class="slider" />
            </label>
        </div>
    </div>
    <div class="bundle-info" :style="quintPackBannerStyle">
        <div v-if="!quintPackActivated" class="promotion-inactive">
            <div class="wordmark">
                <NuxtImg :src="activeQuintPackCopy?.logoSmallSrc" />
            </div>
            <div class="price">
                <div class="normal">
                    {{ $t('SHOPPING_CART.PROMOTIONS.NORMAL_PRICE_LABEL') }}
                    <span class="strikethrough">{{ quintPackNormalPrice }}</span>
                </div>
                <div class="bundled">{{ $t('SHOPPING_CART.PROMOTIONS.BUNDLED_PRICE_LABEL') }} {{ quintPackBundledPrice }}</div>
            </div>
        </div>
        <div v-else class="promotion-active">
            <div class="title">
                <div>{{ $t('SHOPPING_CART.PROMOTIONS.QUINTPACK.INCLUDES_TITLE_LINE_1') }}</div>
                <div>{{ quintPackIncludesTitleLine2 }}</div>
            </div>
            <div class="wordmark-container">
                <div class="wordmarks">
                    <NuxtImg :src="activeQuintPackCopy?.logoSmallSrc" />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import { type ComputedRef, type PropType } from 'vue'
import Shopify from '@jackboxgames/shopify'
import { PromotionalProducts } from '$services/upsell'
import { formatPrice } from '$services/storefronts/storefronts'

type QuintPackTier = 'quintPack1' | 'quintPack2'
type PartyPackConfig = typeof PromotionalProducts.PartyPack1

interface QuintPackCopy {
    name: string
    logoSrc: string
    logoSmallSrc: string
    bannerSrc: string
}
const QUINT_PACK_COPY: Record<QuintPackTier, QuintPackCopy> = {
    quintPack1: {
        name: 'Quintpack',
        logoSrc: '/images/promotions/quintpack/quintpack1-logo.png',
        logoSmallSrc: '/images/promotions/quintpack/quintpack1-logo-small.png',
        bannerSrc: '/images/promotions/quintpack/quintpack1-banner-bg.png'
    },
    quintPack2: {
        name: 'Quintpack 2.0',
        logoSrc: '/images/promotions/quintpack/quintpack2-logo.png',
        logoSmallSrc: '/images/promotions/quintpack/quintpack2-logo-small.png',
        bannerSrc: '/images/promotions/quintpack/quintpack2-banner-bg.png'
    }
}

const props = defineProps({
    quintPackActivated: { type: Boolean, required: true },
    toggleDisabled: { type: Boolean, required: true },
    quintPackTier: { type: String as PropType<QuintPackTier | null>, default: null },
    activeTierPartyPackNumbers: { type: Array as PropType<number[]>, default: () => [] },
    activeTierPartyPackConfigs: { type: Array as PropType<PartyPackConfig[]>, default: () => [] },
    partyPackProducts: { type: Object as PropType<Record<string, Shopify.Shopify.Product | null>>, default: () => ({}) },
    activeQuintPackProduct: { type: Object as PropType<Shopify.Shopify.Product | null>, default: null },
    quintPackNormalPriceNumeric: { type: Number, required: true },
    quintPackBundledPriceNumeric: { type: Number, required: true }
})

defineEmits(['toggle'])

const app = useNuxtApp()

const activeQuintPackCopy: ComputedRef<QuintPackCopy | null> = computed(() => (
    props.quintPackTier ? QUINT_PACK_COPY[props.quintPackTier] : null
))

const quintPackBannerStyle: ComputedRef<Record<string, string>> = computed(() => {
    if (!activeQuintPackCopy.value) { return {} }
    return {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${activeQuintPackCopy.value.bannerSrc}')`
    }
})

const quintPackRangeDescription: ComputedRef<string> = computed(() => {
    const [ppStart] = props.activeTierPartyPackNumbers
    const ppEnd = props.activeTierPartyPackNumbers[props.activeTierPartyPackNumbers.length - 1]
    if (!activeQuintPackCopy.value || ppStart === undefined) { return '' }
    return app.$i18n.t('SHOPPING_CART.PROMOTIONS.QUINTPACK.RANGE_DESCRIPTION', {
        ppStart, ppEnd, quintpackName: activeQuintPackCopy.value.name
    })
})

const quintPackIncludesDescriptionLine2: ComputedRef<string> = computed(() => {
    const [pp1, pp2, pp3, pp4, pp5] = props.activeTierPartyPackNumbers
    if (pp5 === undefined) { return '' }
    return app.$i18n.t('SHOPPING_CART.PROMOTIONS.QUINTPACK.INCLUDES_DESCRIPTION_LINE_2', {
        pp1, pp2, pp3, pp4, pp5
    })
})

const quintPackIncludesTitleLine2: ComputedRef<string> = computed(() => {
    if (!activeQuintPackCopy.value) { return '' }
    return app.$i18n.t('SHOPPING_CART.PROMOTIONS.QUINTPACK.INCLUDES_TITLE_LINE_2', {
        quintpackName: activeQuintPackCopy.value.name
    })
})

const quintPackNormalPrice: ComputedRef<string> = computed(() => {
    const firstFetchedPartyPack = props.activeTierPartyPackConfigs
        .map((config) => props.partyPackProducts[config.variantId])
        .find((product): product is Shopify.Shopify.Product => !!product)
    if (!firstFetchedPartyPack) { return '' }
    const currencyCode = firstFetchedPartyPack.variants[0].price.currencyCode
    return formatPrice(`${props.quintPackNormalPriceNumeric}`, currencyCode, app.$i18n.locale)
})

const quintPackBundledPrice: ComputedRef<string> = computed(() => {
    if (!props.activeQuintPackProduct) { return '' }
    const currencyCode = props.activeQuintPackProduct.variants[0].price.currencyCode
    return formatPrice(`${props.quintPackBundledPriceNumeric}`, currencyCode, app.$i18n.locale)
})
</script>

<style scoped lang="scss">
@use "$styles/kit.scss" as *;

.promotion-container {
    margin: 16px -17px 0 -24px;
    border-top: 1px solid #515E71;
    background: var(--Surface-50, #3A3C43);
}

.promotion {
    display: flex;
    flex-direction: row;
    padding: 0 17px 0 24px;
    @media only screen and (max-width: $mq-cutoff-xsmall) {
        & {
            padding: 0 8px;
        }
    }
    .wordmark {
        width: 94px;
        img {
            width: 83px;
        }
    }
    .description {
        flex: 1;
        padding: 12px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        @media only screen and (max-width: $mq-cutoff-xsmall) {
            & {
                padding: 8px;
                font-size: 14px;
            }
        }
        .header {
            font-weight: 600;
            margin-bottom: 12px;
        }
    }
    .toggle {
        width: 120px;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 22px;

        @media only screen and (max-width: $mq-cutoff-xsmall) {
            & {
                width: 62px;
            }
        }

        /* The switch - the box around the slider */
        .switch {
            position: relative;
            display: inline-block;
            width: 55px;
            height: 22px;

            input {
                opacity: 0;
                width: 0;
                height: 0;
                &:checked + .slider {
                    background-color: #D86500;
                }
                &:focus + .slider {
                    box-shadow: 0 0 1px #D86500;
                }
                &:disabled + .slider {
                    background-color: var(--neutral-600);
                    cursor: not-allowed;
                }
                &:checked + .slider:before {
                    transform: translateX(23px);
                    background-color: #FFBA1A;
                    border: 3px solid #D86500;
                }
                &:disabled + .slider:before {
                    cursor: not-allowed;
                    transform: translateX(23px);
                    background-color: var(--neutral-400);
                    border: 3px solid var(--neutral-500);
                }
            }

        }

        /* The slider */
        .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 16px;
            background-color: var(--neutral-400);
            transition: .25s;
            &:before {
                position: absolute;
                content: "";
                height: 32px;
                width: 32px;
                left: 0;
                bottom: -5px;
                border-radius: 50%;
                background-color: var(--neutral-200);
                border: 3px solid var(--neutral-300);
                transition: .25s;
                filter: drop-shadow(0 4px 6.667px rgba(0, 0, 0, 0.02)) drop-shadow(0 0 2.667px rgba(0, 0, 0, 0.05))
                    drop-shadow(0 1.333px 5.333px rgba(0, 0, 0, 0.08));
            }
        }
    }
}

.bundle-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 17px 0 24px;
    height: 54px;
    background-image: url('/images/promotions/bigbangbundle/bigbang-bot-bg.png');
    border-bottom: 1px solid #515E71;
    background-size: cover;
    .promotion-inactive {
        height: 100%;
        display: flex;
        flex-direction: row;
        gap: 40px;
        .wordmark {
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 80px;
            padding: unset;
            margin-left: 8px;
            img {
                width: 100%;
            }
        }
        .price {
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 6px;
            .normal {
                font-size: 14px;
                font-weight: 500;
                line-height: 14px;
                .strikethrough {
                    text-decoration: line-through;
                }
            }
            .bundled {
                font-size: 16px;
                font-weight: 600;
                line-height: 16px;
            }
        }
    }
    .promotion-active {
        display: flex;
        flex-direction: row;
        height: 100%;
        gap: 16px;
        .title {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            font-size: 16px;
            line-height: 16px;
            font-weight: 600;
        }
        .wordmark-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            .wordmarks {
                display: flex;
                flex-direction: row;
                justify-content: center;
                gap: 4px;
                img {
                    height: 40px;
                }
                .plus {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    font-size: 16px;
                    font-weight: 800;
                    line-height: 16px;
                }
            }
        }
    }
}

.quintpack-promotion {
    .promotion .description .header {
        margin-bottom: 8px;
    }
    .promotion .wordmark {
        width: 130px;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
            width: 116px;
        }
        &.wordmark-compact {
            width: 112px;
            img {
                width: 98px;
            }
        }
    }
    .bundle-info {
        .promotion-inactive .wordmark {
            width: 90px;
        }
        .promotion-active .wordmark-container .wordmarks img {
            height: 40px;
        }
    }
}
</style>
